/* PagingFeed.js */

"use strict";
const EventEmitter = require("events");
const _ = require("lodash");

// Column types whose values come from an options list, so the stored value can be a
// different type than the column itself. Those are the columns valueDataType is for.
const OPTION_BACKED_TYPES = ["array", "enum"];
const NON_STRING_VALUE_TYPES = ["boolean", "number"];

class PagingFeed extends EventEmitter {
    constructor(options) {
        super(options);
        this._userSession = _.get(options, "userSession");
        this._activeOrgId = _.get(
            options,
            "activeOrgId",
            _.get(options, ["userSession", "activeOrganization", "id"])
        );

        this._EntityDef = _.get(options, "entityDef");
        this._relatedEntityDef = _.get(options, "relatedEntityDef");
        this._feedKey = _.get(options, "feedKey", "");
        this._forceNextGridRefresh = true;

        this._filter = _.get(options, "filter", {});
        this._firstPage = 1;
        this._lastPage = 0;
        this._pageSize = _.get(options, "pageSize", 50);
        this._activePage = 1;
        this._totalEntities = 0;

        this._feedReestablished = false;
        this._entityOrder = [];
        this._lastAddedEntities = [];
        this._sort = _.get(options, "sort", {});
        //TODO:: PE-122 -- Need to find a root cause and fix the issue
        // Below is a temporary fix that resets the sort and lets the code continue
        if (typeof this._sort !== "object") {
            console.warn(this._sort, "Sort data error::Not an object");
            this._sort = {};
        }
        if (_.isEmpty(this._sort)) {
            this._sort = { createdOn: 1 };
        } else {
            const sortingKeys = Object.keys(this._sort);
            for(let i=0; i<sortingKeys.length; i++) {
                if (!(this._sort[sortingKeys[i]] === 1 || this._sort[sortingKeys[i]] === -1)) {
                    console.warn(this._sort, "Sort data error::value is not 1 or -1");
                    this._sort = { createdOn: 1 };
                    break;
                }
            }
        }

        this._entityFields = {};
        this._pageLoaded = false;
        this._userColumns = [];
        this.setColumnConfig(
            _.get(options, "columnConfig"),
            _.get(options, "additionalDataFields", [])
        );
        this._searchedEntityId = undefined;
        this._currentPageInfo = {};
        this._lowWaterMark = this._pageSize;
        this._highWaterMark = this._pageSize * 3;

        // Page loading queue
        this._pagesToLoad = [];
        this._rangeIdsUpdatePromise = Promise.resolve();
        this._initialPage = { skip: 0, limit: this._pageSize * 2 };

        // Get initial entity feed object
        this._feed = this._getFeed(options);
        this.reestablish = _.debounce(this.reestablish.bind(this)); //for further consideration
        this._registerListeners();
    }

    setColumnConfig(config, additionalDataFields) {
        this._userColumns.length = 0;

        Object.keys(this._entityFields).forEach((key) => {
            delete this._entityFields[key];
        });
        _.forEach(config, (colDef, colName) => {
            const fieldName =
                config[colName].path ||
                (!_.isBoolean(colDef.entityColumn) && colDef.entityColumn) ||
                colName;
            this._entityFields[fieldName] = true;
            this._userColumns.push(
                _.chain(colDef)
                    .pick(["label", "dataType", "typeInfo", "valuePath", "options", "filterLabel"])
                    .set("name", fieldName)
                    .value()
            );
        });
        additionalDataFields.forEach((fieldName) => (this._entityFields[fieldName] = true));
    }

    getColumnsData() {
        const userColumns = this._userColumns,
            filterProps =
                this._EntityDef.$filterProperties || this._EntityDef.constructor.$filterProperties,
            entityProps = filterProps.reduce(
                (propsObj, prop) => Object.assign(propsObj, { [prop.name]: prop }),
                {}
            );

        // What the entity schema says this column stores. Null for a sub-field the parent
        // schema does not describe, which drops the column below, as it always has.
        const getSchemaType = (fieldName, subFieldName) => {
            if (subFieldName && _.has(entityProps, [fieldName, "schema"])) {
                return (
                    _.get(entityProps, [fieldName, "schema", subFieldName, "dataType"]) ||
                    _.get(entityProps, [
                        fieldName,
                        "schema",
                        "schema",
                        subFieldName,
                        "dataType",
                    ]) ||
                    null
                );
            }
            return _.get(entityProps, [fieldName, "dataType"]);
        };

        return userColumns.reduce((columnsData, columnConfig) => {
            const [fieldName, subFieldName] = columnConfig.name.split(".");
            // The type the column config declares, lower-cased, e.g. "enum" for a select.
            // The && keeps a missing type falsy so the column still drops out below.
            const columnConfigType =
                columnConfig.dataType && String(columnConfig.dataType).toLowerCase();
            const schemaType = getSchemaType(fieldName, subFieldName);
            const valueType = schemaType === undefined ? columnConfigType : schemaType;

            if (!valueType) {
                return columnsData;
            }

            const normalizedValueType = String(valueType).toLowerCase();
            _.defaults(columnConfig, { dataType: normalizedValueType });

            // The type of the values behind an options list, recorded only when it differs from
            // the column type itself. A plain string enum needs nothing here.
            if (
                OPTION_BACKED_TYPES.includes(columnConfigType) &&
                NON_STRING_VALUE_TYPES.includes(normalizedValueType)
            ) {
                columnConfig.valueDataType = normalizedValueType;
            }

            return columnsData.concat(columnConfig);
        }, []);
    }

    setActivePage(newActivePage) {
        this._requestPage(newActivePage);
        this._activePage = newActivePage;
    }

    setSort(sortData, silent) {
        if (!_.isEqual(this._sort, sortData)) {
            //TODO:: PE-122 -- Need to find a root cause and fix the issue
            // Below is a temporary fix that resets the sort and lets the code continue
            // Incase of setSort setting to current sort object
            if (typeof sortData !== "object") {
                console.warn(sortData, "SetSort::Sort data error::Not an object");
                sortData = this._sort;
            }
            const sortingKeys = Object.keys(sortData);
            for (let i = 0; i < sortingKeys.length; i++) {
                if (!(sortData[sortingKeys[i]] === 1 || sortData[sortingKeys[i]] === -1)) {
                    console.warn(this._sort, "SetSort::Sort data error::value is not 1 or -1");
                    sortData = this._sort;
                    break;
                }
            }
            this._sort = sortData;
            if (!silent) {
                this.reestablish();
            }
        } else {
            this.emit("RefreshView", this);
        }
    }

    reestablish() {
        // reset entity order
        this._entityOrder = [];

        // feed reestablished, it returns last page
        this._pageLoaded = false;
        this._feedReestablished = true;
        return this._feedReestablish();
    }

    feedUpdate() {
        this._forceNextGridRefresh = true;
        this._pageLoaded = false;
        return this._feedReestablish().then(() => this.setActivePage(this.activePage));
    }

    getFilterData() {
        return this._filter;
    }

    /**
     * Update filter assuming that $schemeQuery and $systemQuery shouldn't be changed.
     * update only $userQuery section
     * @param query
     * @param silent
     */
    setFilter(query, silent = false) {
        query = query || {};

        const is$userQuery = Object(this._filter).hasOwnProperty("$userQuery");
        const userFilter = is$userQuery ? this._filter.$userQuery : this._filter;
        if (_.isEmpty(query) && _.isEmpty(userFilter)) {
            return;
        }

        this._filter = is$userQuery
            ? Object.assign({}, this._filter, { $userQuery: query })
            : query;
        if (!silent) {
            this.reestablish();
        }
    }

    getPageEntities() {
        return this._feed.cache;
    }

    checkPageInCache(page) {
        for (
            let i = (page - 1) * this._pageSize;
            i < page * this._pageSize && i < this._totalEntities;
            i++
        ) {
            if (!this._feed.cache[this._entityOrder[i]]) {
                return false;
            }
        }
        return true;
    }

    whenFeedReady() {
        return this._feed.whenInitialized().then(() => {
            this.emit("FeedInitialized", this);
            return this;
        });
    }

    wasPreviouslyInitialized() {
        return this._feed.wasPreviouslyInitialized();
    }

    get feed() {
        return this._feed;
    }

    get cache() {
        return this.feed.cache;
    }

    get entityOrder() {
        return this._entityOrder;
    }

    get activePage() {
        return this._activePage;
    }

    get firstPage() {
        return this._firstPage;
    }

    get lastPage() {
        return this._lastPage;
    }

    get totalEntities() {
        return this._totalEntities;
    }

    get pageSize() {
        return this._pageSize;
    }

    getSort() {
        return this._sort;
    }

    get sort() {
        return this._sort;
    }

    isEmpty() {
        return this._totalEntities === 0;
    }

    getEntitiesById(ids) {
        let entities = [];
        ids.forEach((id) => {
            if (this._feed.cache[id]) {
                entities.push(this._feed.cache[id]);
            }
        });
        return entities;
    }

    getEntitiesNameById(ids) {
        return this.getEntitiesValueById(ids, "name");
    }

    getEntitiesValueById(ids, field) {
        let values = [];

        ids.forEach((id) => {
            if (this._feed.cache[id]) {
                values.push(_.get(this._feed.cache[id], field));
            }
        });

        return values;
    }

    getEntitiesValueByIdAsProm(ids, field) {
        return this.getEntitiesValuesByIdAsProm(ids, [field])
            .then((results) => results.map((obj) => obj[field]));
    }

    /**
     * Returns a Promise resolving to an array of objects containing the requested fields
     * for each id in the same order as the provided ids.
     *
     * Example return shape (for fields ["name", "connectionInformation.ip"]):
     * [ { name: "dev1", "connectionInformation.ip": "1.2.3.4" }, ... ]
     *
     *
     * @param {string[]} ids
     * @param {string[]} fields - array of field paths (e.g., ["name", "connectionInformation.ip"]) 
     * @returns {Promise<object[]>}
     */
    getEntitiesValuesByIdAsProm(ids, fields) {
        if (!Array.isArray(ids) || ids.length === 0) {
            return Promise.resolve([]);
        }
        if (!Array.isArray(fields) || fields.length === 0) {
            return Promise.resolve(ids.map(() => ({})));
        }

        // Filter and build projection once
        const validFields = fields.filter(f => typeof f === "string" && f.trim().length > 0);
        if (validFields.length === 0) {
            return Promise.resolve(ids.map(() => ({})));
        }

        const projection = validFields.reduce((proj, f) => {
            proj[f] = true;
            return proj;
        }, {});

        // Helper to extract fields from entity
        const extractFields = (entity) => {
            const picked = {};
            for (let i = 0; i < validFields.length; i++) {
                picked[validFields[i]] = _.get(entity, validFields[i]);
            }
            return picked;
        };

        const resultsById = {};
        const notCachedIds = [];

        // Process all ids in one pass
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const entity = this._feed.cache[id];
            if (entity) {
                resultsById[id] = extractFields(entity);
            } else {
                notCachedIds.push(id);
            }
        }

        if (notCachedIds.length > 0) {
            return this._EntityDef
                .loadByQuery(
                    this._activeOrgId,
                    this._EntityDef.getEntityType(),
                    { id: { $in: notCachedIds } },
                    projection
                )
                .then((entities) => {
                    if (entities) {
                        for (let i = 0; i < entities.length; i++) {
                            const e = entities[i];
                            resultsById[e.id] = extractFields(e);
                        }
                    }
                    return ids.map((id) => resultsById[id]);
                });
        } else {
            return Promise.resolve(ids.map((id) => resultsById[id]));
        }
    }

    getEntitiesByIdAsProm(ids) {
        let promArr = [Promise.resolve()],
            values = [];

        ids.forEach((id) => {
            if (this._feed.cache[id]) {
                values.push(_.get(this._feed.cache, id));
            }
            else {
                promArr.push(this._EntityDef.load(this._activeOrgId, this._EntityDef.getEntityType(), id, {})
                    .then((entity) => {
                        values.push(entity);
                        return Promise.resolve();
                    }));
            }
        });

        return Promise.all(promArr)
            .then(() => {
                return values;
            });
    }

    getEntityById(id) {
        return this._feed.cache[id];
    }

    ensureEntityOnPage(entityId) {
        if (this._feed.cache[entityId]) {
            return Promise.resolve(this._feed.cache[entityId]);
        }
        return this._feed.pageSkipToIdAsProm(this._activeOrgId, entityId).then(() => {
            return new Promise((resolve, reject) => {
                this._waitForEntity(entityId, resolve, reject);
            });
        });
    }

    shutdown() {
        this._unregisterListeners();
        clearTimeout(this._pageRefreshTimeout);
        return this._feedShutDown().then(() => {
            this._filter = undefined;
            this._userSession = undefined;
            this._EntityDef = undefined;
            this._lastAddedEntities = undefined;
            this._entityFields = undefined;
            this._userColumns = undefined;
            this._pageRefreshTimeout = undefined;
        });
    }

    /**
     * Gets list of ids from BE according to the provided range
     * @param {string} endId - range end id
     * @param {string} startId - range start id
     * @param {boolean} [swapIdxOnErr=true] - flag to swap the range start and end in case of error
     * @returns {Promise}
     */
    getRangeSelection(endId, startId, swapIdxOnErr = true) {
        let selection = {
            sort: this._sort,
            range: {
                startId: startId,
                endId: endId
            },
            mongoQuery: this._filter || {}
        };

        return this._getFeedIdsForSelection(this._activeOrgId, selection).catch((err) => {
            // TODO Workaround to be removed once getIdsForSelection() allows swapped indexes for selection
            if (swapIdxOnErr) {
                return this.getRangeSelection(startId, endId, false);
            } else {
                console.error(err);
                return Promise.reject(err);
            }
        });
    }

    updateEntityOrderInRange(endId, startId) {
        let updateEntityOrderPromise = new Promise((resolve, reject) => {
            let startIndex = this._entityOrder.indexOf(startId);
            let endIndex = this._entityOrder.indexOf(endId);

            if (startIndex === -1) {
                console.error(`Could not find start entity index! startId = ${startId}`);
                reject();
                return;
            }

            if (endIndex === -1) {
                console.error(`Could not find end entity index! endId = ${endId}`);
                reject();
                return;
            }

            let selection;
            if (startIndex < endIndex) {
                selection = {
                    sort: this._sort,
                    range: {
                        startId: startId,
                        endId: endId
                    },
                    mongoQuery: this._filter || {}
                };
            } else {
                selection = {
                    sort: this._sort,
                    range: {
                        startId: endId,
                        endId: startId
                    },
                    mongoQuery: this._filter || {}
                };
                let tmp = startIndex;
                startIndex = endIndex;
                endIndex = tmp;
            }

            this._getFeedIdsForSelection(this._activeOrgId, selection).then(
                (res) => {
                    for (let i = startIndex; i <= endIndex; i++) {
                        this._entityOrder[i] = res[i - startIndex];
                    }
                    resolve();
                },
                (err) => {
                    console.error(err);
                    reject();
                }
            );
        });

        this._rangeIdsUpdatePromise = Promise.all([
            this._rangeIdsUpdatePromise,
            updateEntityOrderPromise.catch(() => {})
        ]);

        return updateEntityOrderPromise;
    }

    fetchAllEntityIds() {
        let updateEntityOrderPromise = new Promise((resolve /*, reject*/) => {
            let selection = {
                sort: this._sort,
                range: {
                    all: true
                },
                mongoQuery: this._filter || {}
            };
            this._getFeedIdsForSelection(this._activeOrgId, selection).then(
                (res) => {
                    resolve(res || []);
                },
                (err) => {
                    console.error("Cannot fetch all entity IDs, error %o", err);
                    resolve();
                }
            );
        });

        this._rangeIdsUpdatePromise = Promise.all([
            this._rangeIdsUpdatePromise,
            updateEntityOrderPromise.catch(() => {})
        ]);

        return updateEntityOrderPromise;
    }

    waitForRangeIdsUpdated() {
        return this._rangeIdsUpdatePromise;
    }

    getDistinctValuesOfField(field = null) {
        return field
            ? this._getDistinctValuesOfField(this._activeOrgId, field)
            : Promise.resolve([]);
    }

    _requestPage(page, retryCnt) {
        if (_.isUndefined(page)) {
            page = this._activePage;
        }

        let skip = (page - 1) * this._pageSize;
        if (skip > this.totalEntities || skip < 0) {
            return false;
        }

        // Check if skip request is not already queued
        if (this._pagesToLoad.indexOf(page) !== -1) {
            return false;
        }

        // If waiting another page, queue the request
        if (!this._pageLoaded) {
            // Reducing queue size to one page in order to limit skip requests to the very latest requested page and thus improve performances
            // TODO If no side effects are reported, replace _pagesToLoad array by _nextPageToLoad variable
            // this._pagesToLoad.push(page);
            this._pagesToLoad[0] = page;
            return false;
        }

        // Request skipping
        this._pageLoaded = false;
        this._feed.pageSkip(this._activeOrgId, skip).catch((err) => {
            this._pageLoaded = true;
            // Request next page when we have something in queue
            if (this._pagesToLoad.length > 0) {
                this._requestPage(this._pagesToLoad.shift());
            } else if (
                err &&
                err.errorMsg &&
                err.errorMsg.includes("Entity type write lock timeout")
            ) {
                retryCnt = retryCnt || 0;
                retryCnt++;
                if (retryCnt < 5) {
                    this._requestPage(page, retryCnt);
                }
            }
        });
    }

    _requestEntity(entityId) {
        this._searchedEntityId = undefined;
        const entities = this._feed.getCacheMongoSorted(this._sort);
        if(entities.some((entity) => {
            return entity.id === this._searchedEntityId;
        })) {
            this._feed.pageSkipToId(this._activeOrgId, entityId);
            this._searchedEntityId = entityId;
        }
    }

    _onPage(pageInfo) {
        const feedIsReestablished = this._feedReestablished;
        // a bulk delete under a live feed can report a negative/fractional/NaN total, which would
        // throw RangeError out of the Array() allocation below
        const total = Math.max(0, Math.floor(Number(pageInfo.total) || 0));
        this._totalEntities = total;
        this._lastPage = Math.ceil(total / this._pageSize);

        // fill entity order array with empty ID-values for reserve space
        // this action is required once
        if (this._entityOrder.length) {
            if (this._entityOrder.length < total) {
                for (let i = this._entityOrder.length; i < total; i++) {
                    this._entityOrder.push("");
                }
            }
        } else {
            this._entityOrder = Array(total).fill("");
        }

        // sort entities, using getCacheMongoSorted
        // you can find this function in platform-sdk-remote/.../BaseChangeFeed.js
        const entities = this._feed.getCacheMongoSorted(this._sort);
        let num = pageInfo.before;

        if ((pageInfo.before === 1 && this._activePage === 1) || (pageInfo.after === 1 && this._activePage === this._lastPage - 1)) {
            this._requestPage();
        }

        let foundEntityIdx;
        if (
            this._searchedEntityId &&
            entities &&
            entities.length &&
            entities.some((entity, index) => {
                foundEntityIdx = index;
                return entity.id === this._searchedEntityId;
            })
        ) {
            this.emit("RequestedEntityFound", pageInfo.before + foundEntityIdx);
            this._searchedEntityId = undefined;
            return;
        }

        if (this._feedReestablished) {
            this.emit("FeedReestablished");
            this._feedReestablished = false;
        }

        if (this._forceNextGridRefresh || this._totalEntities < 1 || feedIsReestablished) {
            this._forceNextGridRefresh = false;
            entities.map((dev) => {
                dev.pagePos = num;
                if (this._entityOrder.length > num) {
                    this._entityOrder[num] = dev.id;
                }
                num++;
            });
            this.emit("RefreshView", this);
        }

        this.emit("RefreshLinesNumber", this);

        this._currentPageInfo = pageInfo;

        if (this._isWaterMarkRefreshRequired()) {
            this._requestPage();
        }

        this._pageLoaded = true;

        // request next page when we have something in queue
        if (this._pagesToLoad.length > 0) {
            const nextPage = this._pagesToLoad.shift();
            if ((nextPage - 1) * this._pageSize !== pageInfo.before || this._isWaterMarkRefreshRequired()) {
                this._requestPage(nextPage);
            }
        }
    }

    _isWaterMarkRefreshRequired() {
        return (
            this._currentPageInfo.on > this._highWaterMark ||
            (this._currentPageInfo.on < this._lowWaterMark &&
                this._currentPageInfo.on < this._currentPageInfo.total &&
                this._currentPageInfo.after > 0)
        );
    }

    /**
     * @private - Fired when an Entity has been removed in the system and as a result no longer belongs to this change feed.
     * @param {Entity} entity - The Entity that was removed.
     * @param {string} cause - The cause the entity being removed from this Change Feed.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onEntityRemoved() {
        this._currentPageInfo.on = this._currentPageInfo.on - 1;
        this._forceNextGridRefresh = true;
        if (this._isWaterMarkRefreshRequired()) {
            this._requestPage();
        }
    }

    /**
     * @private - Fired when an Entity has been added in the system.
     * @param {Entity} entity - The Entity that was added.
     * @param {string} cause - The cause the entity being added from this Change Feed.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onEntityAdded() {
        this._currentPageInfo.on = this._currentPageInfo.on + 1;
        this._forceNextGridRefresh = true;
        if (this._isWaterMarkRefreshRequired()) {
            this._requestPage();
        }
    }

    /**
     * @private - Fired when an Entity has been added in the system.
     * @param {Entity} entity - The Entity that was added.
     * @param {string} cause - The cause the entity being added from this Change Feed.
     * @param {EntityChangeFeed} changeFeed - This change feed.
     */
    _onEntityUpdated() {
        this._forceNextGridRefresh = true;
    }

    /**
     * @private - Fired when there's a burst of events on the change feed that have been suppressed by the server
     * requiring the page to be reloaded
     */
    _onRepage() {
        this._requestPage();
    }

    _waitForEntity(entityId, resolve, reject) {
        let checkCnt = 10;
        let interval = setInterval(() => {
            if (this._feed.cache[entityId]) {
                clearInterval(interval);
                resolve(this._feed.cache[entityId]);
            } else if (!--checkCnt) {
                clearInterval(interval);
                reject();
            }
        }, 500);
    }

    _registerListeners() {
        this._feed.on("Page", (this._onPage = this._onPage.bind(this)));
        this._feed.on("Repage", (this._onRepage = this._onRepage.bind(this)));
        this._feed.on("EntityRemoved", (this._onEntityRemoved = this._onEntityRemoved.bind(this)));
        this._feed.on("EntityAdded", (this._onEntityAdded = this._onEntityAdded.bind(this)));
        this._feed.on(
            "EntityPropertiesUpdated",
            (this._onEntityUpdated = this._onEntityUpdated.bind(this))
        );
    }

    _unregisterListeners() {
        this._feed.removeListener("Page", this._onPage);
        this._feed.removeListener("Repage", this._onRepage);
        this._feed.removeListener("EntityRemoved", this._onEntityRemoved);
        this._feed.removeListener("EntityAdded", this._onEntityAdded);
        this._feed.removeListener("EntityPropertiesUpdated", this._onEntityUpdated);
    }

    _feedReestablish() {
        return new Promise((resolve, reject) =>
            this._feed.reestablishFeed(
                this._activeOrgId,
                this._filter,
                this._entityFields,
                this._sort,
                {
                    Success: resolve,
                    Failure: reject
                }
            )
        );
    }

    _feedShutDown() {
        if (_.isUndefined(this._feed)) {
            return Promise.resolve();
        } else {
            return this._feed.shutdown();
        }
    }

    /** methods to be overridden according to the provided EntityDef*/
    _getFeed() {
        console.warn("_getFeed should be overridden");
    }

    _getFeedIdsForSelection() {
        console.warn(
            "_getFeedIdsForSelection should be overridden according to provided EntityDef"
        );
    }

    _getDistinctValuesOfField() {
        //this._EntityDef.getDistinctValuesOfField(this._activeOrgId, field)
        console.warn(
            "_getDistinctValuesOfField should be overridden according to provided EntityDef"
        );
        return Promise.resolve([]);
    }
}

module.exports = PagingFeed;
